import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message, MessageVersion } from '../types';
import FaceSmile from '../../../macadamia-core/icons/FaceSmile';
import FaceFrown from '../../../macadamia-core/icons/FaceFrown';
import Clone from '../../../macadamia-core/icons/Clone';
import JurisprudenceModalWrapper from '../../jurisprudences/components/JurisprudenceModalWrapper';
import LogoLegifrance from '../../../assets/logos/logo-legifrance.svg';
import LogoCoursDeCassation from '../../../assets/logos/logo-cours-de-cassation.svg';
import LogoHalDark from '../../../assets/logos/logo-hal-dark.svg';
import LogoHalWhite from '../../../assets/logos/logo-hal-white.svg';
import './messageStyles.css';

interface MessageItemProps {
    message: Message;
    messageVersions?: MessageVersion[]; // Versions du message (optionnel)
    onRetry?: () => void;
    onFeedback?: (messageId: string, isPositive: boolean | null) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({
    message,
    messageVersions = [],
    onRetry,
    onFeedback
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [selectedJurisprudenceId, setSelectedJurisprudenceId] = useState<string | null>(null);
    const isAssistant = message.sender === 'assistant';
    const [feedbackState, setFeedbackState] = useState<'positive' | 'negative' | null>(
        message.feedbackStatus || null
    );

    // État pour gérer les versions et la version actuellement affichée
    const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
    const [versions, setVersions] = useState<MessageVersion[]>([]);

    // Initialiser ou mettre à jour les versions
    useEffect(() => {
        try {
            // Si on a des versions fournies et qu'elles ne sont pas vides, les utiliser
            if (messageVersions && messageVersions.length > 0) {
                // Pour les messages régénérés, on veut commencer avec la dernière version affichée par défaut
                const versionsToSet = [...messageVersions];
                const isRegeneratedMessage = message.parentMessageId !== undefined;

                // Vérifier si les versions ont changé avant de mettre à jour l'état
                const versionsChanged = JSON.stringify(versionsToSet) !== JSON.stringify(versions);

                if (versionsChanged) {
                    setVersions(versionsToSet);

                    // Pour les messages régénérés ou les messages avec des versions, 
                    // sélectionner automatiquement la dernière version (la plus récente)
                    if (versionsToSet.length > 1 || isRegeneratedMessage) {
                        setCurrentVersionIndex(versionsToSet.length - 1);
                    } else {
                        setCurrentVersionIndex(0);
                    }
                }
                return;
            }

            // Si pas de versions fournies, créer une entrée de version avec le message actuel
            // mais seulement si nécessaire
            if (versions.length === 0 || versions[0]?.content !== message?.content) {
                const initialVersion: MessageVersion = {
                    id: message.id,
                    content: message.content,
                    timestamp: message.timestamp,
                    searchResults: message.searchResults,
                    version: 1
                };
                setVersions([initialVersion]);
                setCurrentVersionIndex(0);
            }
        } catch (error) {
            console.error("Erreur lors de l'initialisation des versions:", error);
            // Ne mettre à jour l'état que si nécessaire
            if (versions.length > 0) {
                setVersions([]);
                setCurrentVersionIndex(0);
            }
        }
    }, [message, messageVersions]);

    // Mise à jour du feedback si le message change
    useEffect(() => {
        if (message && message.feedbackStatus !== undefined && message.feedbackStatus !== feedbackState) {
            setFeedbackState(message.feedbackStatus);
        }
    }, [message?.feedbackStatus, feedbackState]);

    // Gestion du clic sur le feedback
    const handleFeedback = (isPositive: boolean) => {
        // Si on clique sur le même bouton qui est déjà actif, on désactive le feedback
        const newFeedbackState = feedbackState === (isPositive ? 'positive' : 'negative')
            ? null
            : (isPositive ? 'positive' : 'negative');

        setFeedbackState(newFeedbackState);

        if (onFeedback && message) {
            // Passer null au callback si on désactive le feedback
            if (newFeedbackState === null) {
                onFeedback(message.id, null);
            } else {
                onFeedback(message.id, isPositive);
            }
        }
    };

    // Obtenir la version actuellement affichée
    const currentVersion = versions.length > 0 ? versions[currentVersionIndex] || versions[0] : null;

    // Passage à la version précédente
    const goToPreviousVersion = () => {
        if (currentVersionIndex > 0) {
            setCurrentVersionIndex(currentVersionIndex - 1);
        }
    };

    // Passage à la version suivante
    const goToNextVersion = () => {
        if (currentVersionIndex < versions.length - 1) {
            setCurrentVersionIndex(currentVersionIndex + 1);
        }
    };

    // Vérifié si on a plusieurs versions
    const hasMultipleVersions = versions.length > 1;

    // Si nous n'avons pas de version valide à afficher, renvoyer null
    if (!currentVersion) {
        return null;
    }

    return (
    <>
        <div className={`flex w-full mb-10 ${isAssistant ? 'justify-start' : 'justify-end'}`}>
            <div
                className={`relative max-w-3/4 px-4 py-3 rounded-lg ${isAssistant && hasMultipleVersions
                    ? 'rounded-tl-none ' // Angle supérieur gauche à 0 pour les messages avec versions
                    : ''
                    }${isAssistant
                        ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100'
                        : 'bg-mcdm-secondary-400 dark:bg-mcdm-green-400 text-white'
                    }`}
                onMouseEnter={() => isAssistant && setIsHovered(true)}
                onMouseLeave={() => isAssistant && setIsHovered(false)}
            >
                {/* Navigation entre versions (si multiples versions) */}
                {isAssistant && hasMultipleVersions && (
                    <div className="absolute -top-8 left-0 flex items-center space-x-1 bg-white dark:bg-mcdm-third-400 rounded-t-lg px-2 py-1 shadow-md">
                        <button
                            onClick={goToPreviousVersion}
                            disabled={currentVersionIndex === 0}
                            className={`text-xs px-2 py-1 rounded ${currentVersionIndex === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-mcdm-secondary-400 hover:bg-gray-100 dark:hover:bg-mcdm-third-500'
                                }`}
                        >
                            ←
                        </button>
                        <span className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-100">
                            Version {currentVersionIndex + 1}/{versions.length}
                        </span>
                        <button
                            onClick={goToNextVersion}
                            disabled={currentVersionIndex === versions.length - 1}
                            className={`text-xs px-2 py-1 rounded ${currentVersionIndex === versions.length - 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-mcdm-secondary-400 hover:bg-gray-100 dark:hover:bg-mcdm-third-500'
                                }`}
                        >
                            →
                        </button>
                    </div>
                )}

                {/* Contenu du message (version actuelle) avec ReactMarkdown */}
                <div className="break-words overflow-wrap-anywhere">
                    {isAssistant ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none
                            prose-headings:text-mcdm-third-500 dark:prose-headings:text-mcdm-primary-100 prose-headings:font-platypi
                            prose-p:text-mcdm-third-500 dark:prose-p:text-mcdm-primary-100 prose-p:my-2
                            prose-strong:text-mcdm-third-500 dark:prose-strong:text-mcdm-primary-100
                            prose-ul:my-2 prose-ol:my-2 prose-li:my-0
                            prose-a:text-mcdm-secondary-400 hover:prose-a:text-mcdm-secondary-500
                            prose-blockquote:border-mcdm-secondary-400 prose-blockquote:text-mcdm-third-400 dark:prose-blockquote:text-mcdm-primary-200
                            prose-code:bg-gray-100 dark:prose-code:bg-mcdm-third-500 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                            prose-pre:bg-gray-100 dark:prose-pre:bg-mcdm-third-500 prose-pre:p-3 prose-pre:rounded-lg
                            prose-table:text-sm prose-th:bg-gray-100 dark:prose-th:bg-mcdm-third-500 prose-th:px-3 prose-th:py-2 prose-th:text-left
                            prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-gray-200 dark:prose-td:border-gray-600">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {currentVersion.content}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <p className="text-sm break-words whitespace-pre-wrap overflow-wrap-anywhere">
                            {currentVersion.content}
                        </p>
                    )}
                </div>

                {/* Résultats de recherche (de la version actuelle) */}
                {isAssistant && currentVersion.searchResults && currentVersion.searchResults.length > 0 && (
                    <div className="mt-4 space-y-3">
                        {/* Résultats Légifrance */}
                        {currentVersion.searchResults.filter(r => r.source === 'legifrance').length > 0 && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center mb-2">
                                    <img src={LogoLegifrance} alt="Logo Légifrance" style={{ width: '60px' }} className="ml-1" />
                                </div>
                                <div className="space-y-2">
                                    {currentVersion.searchResults
                                        .filter(r => r.source === 'legifrance')
                                        .map((result, index) => (
                                            <div key={`legifrance-${index}`} className="p-2 bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm">
                                                <h5 className="text-xs font-medium">{result.title}</h5>
                                                <p className="text-xs mt-1 text-gray-600 dark:text-gray-300 break-words whitespace-pre-wrap overflow-wrap-anywhere">
                                                    {result.content}
                                                </p>
                                                {result.link && (
                                                    <a
                                                        href={result.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-mcdm-secondary-400 hover:underline mt-1 inline-block"
                                                    >
                                                        Voir la source
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Résultats Jurisprudence - FORMAT STRUCTURÉ */}
                        {currentVersion.searchResults.filter(r => r.source === 'jurisprudence').length > 0 && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center mb-3">
                                    <img src={LogoCoursDeCassation} alt="Logo Cour de Cassation" style={{ width: '60px' }} className="ml-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {currentVersion.searchResults
                                        .filter(r => r.source === 'jurisprudence')
                                        .map((result, index) => (
                                            <div key={`jurisprudence-${index}`} className="p-3 bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm">

                                                {result.isSimplified ? (
                                                    // Affichage pour les enrichissements simplifiés (seulement ID)
                                                    <>
                                                        <button
                                                            onClick={() => setSelectedJurisprudenceId(result.jurisprudenceId || null)}
                                                            className="text-sm font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2 hover:underline cursor-pointer text-left"
                                                        >
                                                            Jurisprudence {result.jurisprudenceId}
                                                        </button>
                                                    </>
                                                ) : (
                                                    // Affichage enrichi avec toutes les données
                                                    <>
                                                        <button
                                                            onClick={() => setSelectedJurisprudenceId(result.jurisprudenceId || null)}
                                                            className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2 font-platypi hover:underline cursor-pointer text-left"
                                                        >
                                                            {result.decision_date && new Date(result.decision_date).toLocaleDateString('fr-FR')}
                                                            {result.decision_date && result.jurisdiction && ' - '}
                                                            {result.jurisdiction}
                                                            {result.chamber && ` - ${result.chamber}`}
                                                            {result.number && ` - n° ${result.number}`}
                                                        </button>

                                                        {result.solution && (
                                                            <div className="mb-2">
                                                                <span className="text-sm font-bold text-mcdm-third-500 dark:text-mcdm-primary-100">Solution : </span>
                                                                <span className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100">{result.solution}</span>
                                                            </div>
                                                        )}

                                                        {result.legalDomain && (
                                                            <div className="mb-2">
                                                                <span className="text-sm font-bold text-mcdm-third-500 dark:text-mcdm-primary-100">Domaine : </span>
                                                                <span className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100">{result.legalDomain}</span>
                                                            </div>
                                                        )}

                                                        {result.issues && result.issues.length > 0 && (
                                                            <div className="mb-2">
                                                                <div className="text-sm font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-1">Problématiques</div>
                                                                <ul className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100">
                                                                    {result.issues.slice(0, 3).map((issue: string, issueIndex: number) => (
                                                                        <li key={issueIndex} className="ml-2">• {issue}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Résultats HAL */}
                        {currentVersion.searchResults.filter(r => r.source === 'hal').length > 0 && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center mb-2">
                                    {/* Logo HAL qui s'adapte au thème clair/sombre */}
                                    <img src={LogoHalDark} alt="Logo HAL" style={{ width: '60px' }} className="ml-1 dark:hidden" />
                                    <img src={LogoHalWhite} alt="Logo HAL" style={{ width: '60px' }} className="ml-1 hidden dark:block" />
                                </div>
                                <div className="space-y-2">
                                    {currentVersion.searchResults
                                        .filter(r => r.source === 'hal')
                                        .map((result, index) => (
                                            <div key={`hal-${index}`} className="p-2 bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm">
                                                <h5 className="text-xs font-medium">{result.title}</h5>
                                                <p className="text-xs mt-1 text-gray-600 dark:text-gray-300 break-words whitespace-pre-wrap overflow-wrap-anywhere">
                                                    {result.content}
                                                </p>
                                                {result.link && (
                                                    <a
                                                        href={result.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-mcdm-secondary-400 hover:underline mt-1 inline-block"
                                                    >
                                                        Voir la source
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="text-xs mt-1 opacity-70">
                    {currentVersion.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>

                {/* Contrôles de feedback et de régénération */}
                {isAssistant && (isHovered || feedbackState !== null) && (
                    <div className="absolute -bottom-8 right-0 flex items-center space-x-2 bg-white dark:bg-mcdm-third-400 rounded-lg py-1 px-2 shadow-md">
                        <button
                            onClick={() => handleFeedback(true)}
                            className={`transition-colors ${feedbackState === 'positive'
                                ? 'text-mcdm-green-400 hover:text-mcdm-green-500'
                                : 'text-mcdm-secondary-400 hover:text-mcdm-secondary-200'
                                }`}
                            title={feedbackState === 'positive' ? "Désactiver le feedback positif" : "Feedback positif"}
                            aria-pressed={feedbackState === 'positive'}
                        >
                            <FaceSmile className="w-6 h-6" />
                        </button>
                        <button
                            onClick={() => handleFeedback(false)}
                            className={`transition-colors ${feedbackState === 'negative'
                                ? 'text-red-500 hover:text-red-600'
                                : 'text-mcdm-secondary-400 hover:text-mcdm-secondary-200'
                                }`}
                            title={feedbackState === 'negative' ? "Désactiver le feedback négatif" : "Feedback négatif"}
                            aria-pressed={feedbackState === 'negative'}
                        >
                            <FaceFrown className="w-6 h-6" />
                        </button>
                        <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                        <button
                            onClick={onRetry}
                            className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 hover:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 transition-colors"
                        >
                            Réessayer
                        </button>
                        <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                        <button
                            onClick={() => navigator.clipboard.writeText(currentVersion.content)}
                            className="flex items-center space-x-1 text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 hover:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 transition-colors"
                            title="Copier la réponse"
                        >
                            <Clone className="w-4 h-4" />
                            <span>Copier</span>
                        </button>
                    </div>
                )}
            </div>
        </div>

        <JurisprudenceModalWrapper
            jurisprudenceId={selectedJurisprudenceId}
            isOpen={!!selectedJurisprudenceId}
            onClose={() => setSelectedJurisprudenceId(null)}
        />
    </>
    );
};

export default MessageItem;